import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useNotify from '../../hooks/useNotify';
import {
    RELEASE_TYPE,
    VISIT_END,
    apiError,
    dayOf,
    fetchLinkQueue,
    gateLabel,
    imageUrl,
    saveLink,
    searchVisits,
} from '../../helpers/parkingEvents';
import DiffPlate, { plateSx } from './DiffPlate';
import LinkDialog from './LinkDialog';
import NoteDialog from './NoteDialog';
import Reasons from './Reasons';

const DAY = 864e5;
const daysBack = (n) => dayOf(Date.now() - n * DAY);
const RANGES = [
    { label: 'Ma', from: () => daysBack(0) },
    { label: 'Tegnap óta', from: () => daysBack(1) },
    { label: '7 nap', from: () => daysBack(6) },
];
const huf = (n) => `${new Intl.NumberFormat('hu-HU').format(n || 0)} Ft`;
const normPlate = (s) =>
    String(s || '')
        .toUpperCase()
        .replace(/[^0-9A-Z]/g, '');

// the release as the item side of the link dialog
export function releaseItem(r) {
    return {
        type: 'release',
        ref: r.id || r.k,
        title: RELEASE_TYPE[r.type] || 'Kiengedés',
        plate: r.after ? r.after.plate || '(nem olvasta)' : '–',
        lines: [
            `${r.time.slice(0, 16)} · ${gateLabel(r.gate)}`,
            r.user ? `be.web: ${r.user}` : null,
            r.ticket ? `jegy: ${r.ticket}` : null,
            r.after
                ? `előtte ${r.after.type} (${r.after.time.slice(11)})`
                : 'nem előzte meg elutasítás',
        ],
    };
}

function ReleaseHead({ r }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="baseline" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle2">{r.time.slice(5, 16)}</Typography>
            <Chip size="small" label={RELEASE_TYPE[r.type] || r.type} />
            <Typography variant="body2">{gateLabel(r.gate)}</Typography>
            {r.after && (
                <Typography variant="body2" color="text.secondary">
                    elutasítva:{' '}
                    <Box component="span" sx={{ ...plateSx, color: 'text.primary' }}>
                        {r.after.plate || '(nem olvasta)'}
                    </Box>{' '}
                    · {r.after.type} · {r.after.time.slice(11)}
                </Typography>
            )}
            {r.user && (
                <Typography variant="caption" color="text.secondary">
                    be.web: {r.user}
                </Typography>
            )}
        </Stack>
    );
}

function Thumb({ point }) {
    const url = imageUrl(point);
    return (
        <Box
            sx={{
                width: 96,
                aspectRatio: '16 / 9',
                bgcolor: url ? '#000' : 'action.hover',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {url ? (
                <Box
                    component="img"
                    src={url}
                    alt=""
                    loading="lazy"
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            ) : (
                <Typography variant="caption" color="text.secondary">
                    nincs kép
                </Typography>
            )}
        </Box>
    );
}

// a parking as a candidate line: image, plate (difference marked), entry, reasons, actions
function CandidateRow({ c, onOpen, onLink }) {
    const v = c.visit;
    const end = VISIT_END[v.end] || { label: v.end, color: 'default' };
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1 }}>
            <Thumb point={v.in} />
            <Box sx={{ minWidth: 140 }}>
                <Typography sx={{ ...plateSx, fontSize: 16 }}>
                    <DiffPlate plate={v.plate || '–'} diff={c.diff} />
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                    {v.in.time.slice(5, 16)} · {gateLabel(v.in.gate)}
                </Typography>
                <Chip size="small" color={end.color} label={end.label} sx={{ mt: 0.5 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
                <Reasons reasons={c.reasons} dense />
            </Box>
            <Stack spacing={0.5}>
                <Button size="small" variant="contained" onClick={onLink}>
                    Összekapcsolás…
                </Button>
                <Button size="small" onClick={onOpen}>
                    Megnyitás
                </Button>
            </Stack>
        </Stack>
    );
}

// a release without a car: search the parkings open at the time by a plate (the camera image, what the guard knows)
function FindDialog({ release, onClose, onPick }) {
    const [plate, setPlate] = useState('');
    const [state, setState] = useState({ loading: false, rows: null, error: null });
    useEffect(() => {
        if (release) {
            setPlate(release.after ? release.after.plate || '' : '');
            setState({ loading: false, rows: null, error: null });
        }
    }, [release]);
    if (!release) return null;
    const day = release.time.slice(0, 10);
    const search = () => {
        const q = normPlate(plate);
        if (q.length < 3) return;
        setState({ loading: true, rows: null, error: null });
        searchVisits({
            plate: q,
            from: dayOf(new Date(`${day}T12:00:00`).getTime() - 8 * DAY),
            to: day,
            limit: 50,
        })
            .then((d) => {
                const before = (v) => v.in.time < release.time;
                const rows = [
                    ...d.exact.filter(before).map((visit) => ({ visit })),
                    ...d.similar.filter((s) => before(s.visit)),
                ];
                setState({ loading: false, rows, error: null });
            })
            .catch((error) => setState({ loading: false, rows: null, error }));
    };
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                Melyik parkolás? – {release.time.slice(5, 16)} {gateLabel(release.gate)}
            </DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    size="small"
                    placeholder="Rendszám (a képről, az ügyféltől)"
                    value={plate}
                    onChange={(e) => setPlate(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === 'Enter' && search()}
                    sx={{ mt: 1, minWidth: 280 }}
                    InputProps={{
                        sx: { fontFamily: 'monospace', fontWeight: 700 },
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button onClick={search} sx={{ mt: 1, ml: 1 }}>
                    Keresés
                </Button>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                >
                    A kiengedés előtti 8 nap behajtásai, pontos és 1 karakter eltérésű rendszámok.
                </Typography>
                {state.loading && <CircularProgress size={20} sx={{ mt: 2 }} />}
                {state.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {apiError(state.error)}
                    </Alert>
                )}
                {state.rows && !state.rows.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Nincs találat.
                    </Typography>
                )}
                {state.rows && state.rows.length > 0 && (
                    <Table size="small" sx={{ mt: 1 }}>
                        <TableBody>
                            {state.rows.map(({ visit: v, diff }) => {
                                const end = VISIT_END[v.end] || { label: v.end, color: 'default' };
                                return (
                                    <TableRow key={v.id}>
                                        <TableCell>
                                            <Thumb point={v.in} />
                                        </TableCell>
                                        <TableCell sx={plateSx}>
                                            <DiffPlate plate={v.plate || '–'} diff={diff} />
                                        </TableCell>
                                        <TableCell>
                                            {v.in.time.slice(5, 16)} · {gateLabel(v.in.gate)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                color={end.color}
                                                label={end.label}
                                            />
                                        </TableCell>
                                        <TableCell>{v.paid ? huf(v.paid) : ''}</TableCell>
                                        <TableCell align="right">
                                            <Button size="small" onClick={() => onPick(v, diff)}>
                                                Ez az
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Bezárás</Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * Teendők: releases of the last days that need a person. "Összekapcsolásra vár": the parkings suggested for the
 * rejected plate; "Hozzárendelésre vár": no candidate / no rejection before (search by hand or close as not
 * identifiable). Releases right after a rejection of the car's own parking are linked by the system.
 */
export default function QueueTab({ version, onChanged, onOpen }) {
    const { notifyError, notifySuccess } = useNotify();
    const [range, setRange] = useState(1);
    const [state, setState] = useState({ loading: false, data: null, error: null });
    const [dialog, setDialog] = useState(null); // {visit, item, diff, reasons, canReject}
    const [find, setFind] = useState(null); // release
    const [closing, setClosing] = useState(null); // release

    useEffect(() => {
        let stop = false;
        setState((s) => ({ ...s, loading: true, error: null }));
        fetchLinkQueue({ from: RANGES[range].from(), to: daysBack(0) })
            .then((data) => !stop && setState({ loading: false, data, error: null }))
            .catch((error) => !stop && setState({ loading: false, data: null, error }));
        return () => {
            stop = true;
        };
    }, [range, version]);

    const { data } = state;
    return (
        <>
            <Grid item xs={12}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {RANGES.map((r, i) => (
                        <Chip
                            key={r.label}
                            label={r.label}
                            color={range === i ? 'primary' : 'default'}
                            variant={range === i ? 'filled' : 'outlined'}
                            onClick={() => setRange(i)}
                        />
                    ))}
                    {state.loading && <CircularProgress size={20} />}
                    {data && (
                        <Typography variant="body2" color="text.secondary">
                            {data.done} kiengedés rendezett (a rendszer vagy egy kolléga
                            összekapcsolta / lezárta)
                        </Typography>
                    )}
                </Stack>
            </Grid>
            {state.error && (
                <Grid item xs={12}>
                    <Alert severity="error">{apiError(state.error)}</Alert>
                </Grid>
            )}
            {data && data.missingDays.length > 0 && (
                <Grid item xs={12}>
                    <Alert severity="info">
                        Még nincs feldolgozva: {data.missingDays.join(', ')}
                    </Alert>
                </Grid>
            )}
            {data && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Összekapcsolásra vár · {data.suggested.length}
                        </Typography>
                        {!data.suggested.length && (
                            <Typography variant="body2" color="text.secondary">
                                Nincs javaslat.
                            </Typography>
                        )}
                        {data.suggested.map(({ release: r, candidates }) => (
                            <Box
                                key={r.id}
                                sx={{ borderTop: 1, borderColor: 'divider', pt: 1.5, mt: 1.5 }}
                            >
                                <ReleaseHead r={r} />
                                {candidates.map((c) => (
                                    <CandidateRow
                                        key={c.visit.id}
                                        c={c}
                                        onOpen={() => onOpen(c.visit.id)}
                                        onLink={() =>
                                            setDialog({
                                                visit: c.visit,
                                                item: releaseItem(r),
                                                diff: c.diff,
                                                reasons: c.reasons,
                                                canReject: true,
                                            })
                                        }
                                    />
                                ))}
                            </Box>
                        ))}
                    </Paper>
                </Grid>
            )}
            {data && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                            Hozzárendelésre vár · {data.waiting.length}
                        </Typography>
                        {!data.waiting.length && (
                            <Typography variant="body2" color="text.secondary">
                                Nincs ilyen kiengedés.
                            </Typography>
                        )}
                        {data.waiting.map(({ release: r, reason }) => (
                            <Stack
                                key={r.id}
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                alignItems={{ md: 'center' }}
                                sx={{ borderTop: 1, borderColor: 'divider', py: 1 }}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <ReleaseHead r={r} />
                                    <Typography variant="caption" color="text.secondary">
                                        {reason}
                                    </Typography>
                                </Box>
                                <Stack direction="row" spacing={1}>
                                    <Button size="small" onClick={() => setFind(r)}>
                                        Parkolás keresése…
                                    </Button>
                                    <Button
                                        size="small"
                                        color="inherit"
                                        onClick={() => setClosing(r)}
                                    >
                                        Nem azonosítható
                                    </Button>
                                </Stack>
                            </Stack>
                        ))}
                    </Paper>
                </Grid>
            )}

            <FindDialog
                release={find}
                onClose={() => setFind(null)}
                onPick={(visit, diff) => {
                    setDialog({
                        visit,
                        item: releaseItem(find),
                        diff,
                        reasons: [],
                        canReject: false,
                    });
                    setFind(null);
                }}
            />
            <LinkDialog
                open={!!dialog}
                {...(dialog || {})}
                onClose={() => setDialog(null)}
                onDone={() => {
                    setDialog(null);
                    onChanged();
                }}
            />
            <NoteDialog
                open={!!closing}
                title="Nem azonosítható kiengedés"
                label="Miért nem azonosítható?"
                info="A kiengedés autó nélkül, indoklással lezárul (a kimutatásban így szerepel). Később is összekapcsolható, ha kiderül."
                action="Lezárás"
                onClose={() => setClosing(null)}
                onSubmit={(note) =>
                    saveLink({
                        action: 'unidentified',
                        itemType: 'release',
                        itemRef: closing.id,
                        note,
                    })
                        .then(() => {
                            notifySuccess('Lezárva: nem azonosítható');
                            setClosing(null);
                            onChanged();
                        })
                        .catch((e) => notifyError(apiError(e)))
                }
            />
        </>
    );
}
